import { useEffect, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { Check, CircleAlert, Loader } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@ui/Button";
import { formSessionAtom, type FormSession } from "@/state/formSession";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { URLS } from "@/constants/urls";
import { isAlreadyPaidCheckoutError } from "@/utils/errors";
import { clearPaymentSessionStore, readPaymentSessionFromWindowName } from "@/utils/formSession";
import { CustomerApi } from "@/api/CustomerApi";
import { PaymentsApi } from "@/api/PaymentsApi";
import SectionFetchError from "@/components/SectionFetchError";

type PaymentStatusType = "success" | "error";

interface PaymentStatusProps {
  status: PaymentStatusType;
}

export const PaymentStatus: FC<PaymentStatusProps> = ({ status }) => {
  const { customerId: inMemoryCustomerId } = useAtomValue(formSessionAtom);
  const paymentSession = readPaymentSessionFromWindowName();
  const customerId = inMemoryCustomerId ?? paymentSession.customerId;
  const navigate = useNavigate();

  const shouldRedirect = !customerId && status === "success";

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: [QUERY_KEYS.CUSTOMER, { customerId }],
    queryFn: () => CustomerApi.getOne(customerId ?? ""),
    enabled: !shouldRedirect,
  });

  useEffect(
    function redirectIfNoCustomerId() {
      if (shouldRedirect) {
        void navigate(URLS.HOME, { replace: true });
      }
    },
    [navigate, shouldRedirect],
  );

  if (shouldRedirect) return null;

  return (
    <div className="flex h-full flex-col items-start">
      {status === "error" ? (
        <PaymentStatusError paymentSession={paymentSession} />
      ) : (
        <>
          {isPending ? (
            <Loader className="m-auto animate-spin" data-test="payment-status-loader" />
          ) : isError ? (
            <SectionFetchError
              refetch={() => void refetch()}
              data-test="payment-status-fetch-error"
            />
          ) : (
            <PaymentStatusSuccess email={data.email} />
          )}
        </>
      )}
    </div>
  );
};

const PaymentStatusSuccess: FC<{ email: string }> = ({ email }) => {
  const { t } = useTranslation();

  useEffect(() => {
    clearPaymentSessionStore();
  }, []);

  return (
    <div
      data-test="payment-status-success"
      className="flex h-full w-full flex-col justify-center gap-16"
    >
      <div
        className="mt-1 text-center text-3xl leading-none font-bold lg:text-left lg:text-[34px]"
        data-test="payment-status-success-title"
      >
        <h2 className="mb-4">{t("paymentSuccessTitle")}</h2>
        <h2>{t("thanksForUsingCalculator")}</h2>
      </div>
      <p className="text-lg lg:text-2xl" data-test="payment-status-success-description">
        {t("paymentSuccessDescription")}
      </p>
      <div
        className="flex items-center justify-center gap-4"
        data-test="payment-status-success-email-row"
      >
        <div
          className="bg-primary flex size-8 items-center justify-center rounded-full text-white"
          data-test="payment-status-success-check"
        >
          <Check strokeWidth={1.5} />
        </div>
        <p className="text-lg font-bold lg:text-2xl" data-test="payment-status-success-email">
          {email}
        </p>
      </div>
      <p className="text-lg lg:text-2xl" data-test="payment-status-success-processing">
        {t("processingTimeInfo")}
      </p>
      <p className="text-lg lg:text-2xl" data-test="payment-status-success-thanks">
        {t("thanksForTrust")}
      </p>
    </div>
  );
};

const PaymentStatusError: FC<{ paymentSession: FormSession }> = ({ paymentSession }) => {
  const { t } = useTranslation();
  const { formId: inMemoryFormId } = useAtomValue(formSessionAtom);
  const formId = inMemoryFormId ?? paymentSession.formId;

  const { mutate: createCheckoutSession, isPending } = useMutation({
    mutationFn: () => PaymentsApi.createCheckoutSession(formId ?? ""),
    onError: error =>
      toast.error(
        isAlreadyPaidCheckoutError(error)
          ? t("checkoutAlreadyPaidError")
          : t("checkoutSessionError"),
      ),
    onSuccess: session => window.location.assign(session.checkout_url),
  });

  return (
    <div data-test="payment-status-error" className="flex h-full w-full flex-col gap-16">
      <div
        className="flex flex-col items-center gap-4 lg:flex-row"
        data-test="payment-status-error-header"
      >
        <CircleAlert className="text-destructive size-9" data-test="payment-status-error-icon" />
        <h2
          className="text-center text-3xl font-bold lg:text-left lg:text-[34px]"
          data-test="payment-status-error-title"
        >
          {t("paymentFailureTitle")}
        </h2>
      </div>
      <p className="text-lg lg:text-2xl" data-test="payment-status-error-description">
        {t("paymentFailureDescription")}
      </p>
      <Button
        onClick={() => createCheckoutSession()}
        disabled={isPending}
        className="mx-auto max-w-full px-5 uppercase min-[400px]:w-min lg:text-base"
        data-test="payment-status-retry-button"
      >
        {t("retryPayment")}
      </Button>
      <p className="text-lg lg:text-2xl" data-test="payment-status-error-contact">
        {t("contactInfo")}
      </p>
    </div>
  );
};
